import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Users, Package, Send, Trash2, Plus, UserCheck, X, Box, ScanBarcode } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';
import { validateStaffId } from '@/services/validationService';

const GreigePacking = () => {
  const navigate = useNavigate();
  const staffInputRef = useRef(null);
  const bundleInputRef = useRef(null);

  const [step, setStep] = useState('staff');

  // Multi-staff
  const [staffValue, setStaffValue] = useState('');
  const [staffList, setStaffList] = useState([]);
  const [validating, setValidating] = useState(false);

  // Bundles
  const [bundleCode, setBundleCode] = useState('');
  const [scannedBundles, setScannedBundles] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (step === 'staff') setTimeout(() => staffInputRef.current?.focus(), 80);
    else if (step === 'scan') setTimeout(() => bundleInputRef.current?.focus(), 80);
  }, [step]);

  const handleAddStaff = async (e) => {
    e.preventDefault();
    const id = staffValue.trim();
    if (!id || validating) return;
    if (staffList.some((s) => s.staff_id === id)) {
      toast.error('Staff already added');
      setStaffValue('');
      staffInputRef.current?.focus();
      return;
    }
    setValidating(true);
    try {
      const res = await validateStaffId(id);
      if (!res.valid) {
        toast.error(res.message || 'Invalid Staff ID');
        return;
      }
      setStaffList((prev) => [...prev, { staff_id: res.staffId, staff_name: res.staffName || '' }]);
      setStaffValue('');
      toast.success(`Added ${res.staffName || res.staffId}`);
    } finally {
      setValidating(false);
      setTimeout(() => staffInputRef.current?.focus(), 80);
    }
  };

  const handleRemoveStaff = (staff_id) => {
    setStaffList((prev) => prev.filter((s) => s.staff_id !== staff_id));
  };

  const handleProceedToScan = () => {
    if (staffList.length === 0) {
      toast.error('Add at least one staff');
      return;
    }
    setStep('scan');
  };

  const handleBundleScan = async (e) => {
    e.preventDefault();
    const code = bundleCode.trim();
    if (!code) {
      toast.error('Please scan a bundle barcode');
      bundleInputRef.current?.focus();
      return;
    }
    if (scannedBundles.some((b) => b.bundle_barcode === code)) {
      toast.error('Bundle already scanned');
      setBundleCode('');
      bundleInputRef.current?.focus();
      return;
    }

    setIsProcessing(true);
    try {
      const response = await apiClient.post(
        `/sections/greige/packing/validate-bundle?bundle_barcode=${encodeURIComponent(code)}`
      );
      if (response.data.valid) {
        const colorName = response.data.color_name || '';
        const sortNo = response.data.sort_no || '';
        let usnCount = 0;
        let markerQty = 0;
        try {
          const c = await apiClient.get(
            `/sections/greige/bundle-usn-count/${encodeURIComponent(code)}`
          );
          usnCount = c.data?.usn_count || 0;
          markerQty = c.data?.marker_qty || 0;
        } catch {
          // silent — preview-only data
        }
        setScannedBundles((prev) => [
          { bundle_barcode: code, usn_count: usnCount, marker_qty: markerQty, color_name: colorName, sort_no: sortNo },
          ...prev,
        ]);
        toast.success(`Bundle added — ${markerQty} pcs (Marker Writing Qty)`);
      }
      setBundleCode('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Bundle validation failed');
      setBundleCode('');
    } finally {
      setIsProcessing(false);
      setTimeout(() => bundleInputRef.current?.focus(), 80);
    }
  };

  const handleRemoveBundle = (bundle_barcode) => {
    setScannedBundles((prev) => prev.filter((b) => b.bundle_barcode !== bundle_barcode));
  };

  const totalUsns = useMemo(
    () => scannedBundles.reduce((sum, b) => sum + (b.usn_count || 0), 0),
    [scannedBundles]
  );

  const totalMarkerQty = useMemo(
    () => scannedBundles.reduce((sum, b) => sum + (b.marker_qty || 0), 0),
    [scannedBundles]
  );

  const splitPreview = useMemo(() => {
    const n = staffList.length;
    if (n === 0) return [];
    const base = Math.floor(totalUsns / n);
    const remainder = totalUsns - base * n;
    return staffList.map((s, i) => ({ ...s, attributed_qty: base + (i < remainder ? 1 : 0) }));
  }, [staffList, totalUsns]);

  const handleSubmit = async () => {
    if (scannedBundles.length === 0) {
      toast.error('Scan at least one bundle');
      return;
    }
    if (staffList.length === 0) {
      toast.error('Add at least one staff');
      return;
    }
    setIsSubmitting(true);
    try {
      const res = await apiClient.post('/sections/greige/packing/submit', {
        staff_list: staffList.map((s) => ({ staff_id: s.staff_id, staff_name: s.staff_name })),
        bundle_barcodes: scannedBundles.map((b) => b.bundle_barcode),
      });
      const breakdown = (res.data?.staff_breakdown || [])
        .map((s) => `${s.staff_name || s.staff_id}: ${s.attributed_qty}`)
        .join(', ');
      toast.success(
        `Packing recorded — ${res.data.bundle_count} bundle(s), ${res.data.usn_count} USN(s)` +
          (breakdown ? ` (${breakdown})` : '')
      );
      setScannedBundles([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit packing');
    } finally {
      setIsSubmitting(false);
      setTimeout(() => bundleInputRef.current?.focus(), 80);
    }
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]" data-testid="greige-packing-page">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/sections/greige')}
              className="text-[#F9FAFB] hover:bg-white/10"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold font-display flex items-center gap-2">
                <Box className="w-5 h-5" /> Operation 8: Packing
              </h1>
              <p className="text-sm text-[#E8DCC8]">Greige Section — multi-staff supported</p>
            </div>
          </div>
          {staffList.length > 0 && (
            <div
              className="hidden sm:flex items-center gap-2 bg-teal-600/20 px-3 py-1.5 rounded-lg"
              data-testid="staff-count-badge"
            >
              <Users className="w-4 h-4 text-teal-200" />
              <span className="text-sm font-medium text-teal-100">{staffList.length} staff</span>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 md:px-6 py-6 space-y-6">
        {step === 'staff' && (
          <Card className="border-2 border-teal-300">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Users className="w-5 h-5 text-teal-600" />
                Add Staff Working on Packing
              </CardTitle>
              <CardDescription>
                Scan or type one or more Staff IDs. Total USN qty across the scanned bundles will be split equally between them (remainder goes to the earliest staff).
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <form onSubmit={handleAddStaff} className="flex gap-2">
                <Input
                  ref={staffInputRef}
                  value={staffValue}
                  onChange={(e) => setStaffValue(e.target.value)}
                  placeholder="Scan or type Staff ID"
                  className="text-lg h-12 font-mono"
                  autoFocus
                  disabled={validating}
                  data-testid="staff-input"
                />
                <Button
                  type="submit"
                  className="h-12 px-6 bg-teal-600 hover:bg-teal-700"
                  disabled={validating || !staffValue.trim()}
                  data-testid="staff-add-btn"
                >
                  {validating ? '…' : <><Plus className="w-4 h-4 mr-1" />Add</>}
                </Button>
              </form>

              {staffList.length > 0 && (
                <div className="space-y-1.5" data-testid="staff-list">
                  <p className="text-xs text-gray-500 font-medium">{staffList.length} staff added:</p>
                  {staffList.map((s) => (
                    <div
                      key={s.staff_id}
                      className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-3 py-2"
                      data-testid={`staff-chip-${s.staff_id}`}
                    >
                      <UserCheck className="w-4 h-4 text-green-600" />
                      <span className="font-medium text-green-800 text-sm flex-1">{s.staff_name}</span>
                      <span className="text-xs text-gray-500 font-mono">{s.staff_id}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                        onClick={() => handleRemoveStaff(s.staff_id)}
                        data-testid={`staff-remove-${s.staff_id}`}
                      >
                        <X className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              <Button
                className="w-full bg-teal-600 hover:bg-teal-700"
                size="lg"
                onClick={handleProceedToScan}
                disabled={staffList.length === 0}
                data-testid="proceed-to-scan-btn"
              >
                <ScanBarcode className="w-4 h-4 mr-2" />
                Proceed to Scan Bundles ({staffList.length} staff)
              </Button>
            </CardContent>
          </Card>
        )}

        {step === 'scan' && (
          <>
            <div
              className="flex items-center gap-2 bg-teal-50 border border-teal-200 rounded-lg px-3 py-2 text-sm flex-wrap"
              data-testid="active-staff-bar"
            >
              <Users className="w-4 h-4 text-teal-600 shrink-0" />
              <span className="text-teal-900 font-medium">Working as:</span>
              {staffList.map((s, i) => (
                <React.Fragment key={s.staff_id}>
                  {i > 0 && <span className="text-gray-400">·</span>}
                  <Badge variant="secondary" className="bg-teal-100 text-teal-800">
                    {s.staff_name || s.staff_id}
                  </Badge>
                </React.Fragment>
              ))}
              <Button
                variant="ghost"
                size="sm"
                className="ml-auto h-7 text-xs"
                onClick={() => setStep('staff')}
                data-testid="change-staff-btn"
              >
                Change staff
              </Button>
            </div>

            <Card className="border-2 border-teal-600">
              <CardHeader className="pb-3 bg-teal-50">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Package className="w-5 h-5 text-teal-600" />
                  Scan Bundle Barcodes
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-4">
                <form onSubmit={handleBundleScan} className="flex gap-3">
                  <Input
                    ref={bundleInputRef}
                    value={bundleCode}
                    onChange={(e) => setBundleCode(e.target.value)}
                    placeholder="Scan Bundle QR code…"
                    className="text-lg h-14 font-mono"
                    disabled={isProcessing}
                    autoComplete="off"
                    data-testid="bundle-input"
                  />
                  <Button
                    type="submit"
                    className="h-14 px-8 bg-teal-600 hover:bg-teal-700"
                    disabled={isProcessing}
                    data-testid="bundle-add-btn"
                  >
                    <Package className="w-5 h-5 mr-2" />
                    Add
                  </Button>
                </form>
              </CardContent>
            </Card>

            {scannedBundles.length > 0 && (
              <Card className="border-2 border-[#E5DFD0]">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-lg" data-testid="scanned-bundles-title">
                        Scanned Bundles ({scannedBundles.length}) · {totalMarkerQty} pcs (Marker Writing Qty)
                      </CardTitle>
                      <CardDescription>Latest scans on top</CardDescription>
                    </div>
                    <Button
                      onClick={handleSubmit}
                      disabled={isSubmitting}
                      className="bg-teal-600 hover:bg-teal-700"
                      data-testid="submit-btn"
                    >
                      {isSubmitting ? (
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                      ) : (
                        <Send className="w-4 h-4 mr-2" />
                      )}
                      Submit
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {splitPreview.length > 0 && totalUsns > 0 && (
                    <div className="mb-3 p-3 bg-teal-50 rounded-lg" data-testid="split-preview">
                      <p className="text-xs font-semibold text-teal-900 mb-2">Qty split preview</p>
                      <div className="flex flex-wrap gap-2">
                        {splitPreview.map((s) => (
                          <Badge
                            key={s.staff_id}
                            variant="outline"
                            className="bg-white border-teal-300 text-teal-900 px-2 py-1"
                            data-testid={`split-preview-${s.staff_id}`}
                          >
                            {s.staff_name || s.staff_id}: {s.attributed_qty} USN(s)
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="space-y-2 max-h-80 overflow-y-auto">
                    {scannedBundles.map((b, index) => (
                      <div
                        key={b.bundle_barcode}
                        className="flex items-center justify-between p-3 bg-[#F5F1E8] rounded-lg"
                        data-testid={`bundle-row-${b.bundle_barcode}`}
                      >
                        <div className="flex items-center gap-4">
                          <span className="text-sm text-[#5A5F6E] w-8">#{scannedBundles.length - index}</span>
                          <p className="font-mono font-semibold text-[#1A1F2E]">{b.bundle_barcode}</p>
                          {b.color_name && (
                            <Badge variant="secondary" className="text-xs bg-cyan-100 text-cyan-800" data-testid={`bundle-color-${b.bundle_barcode}`}>Color: {b.color_name}</Badge>
                          )}
                          <Badge variant="secondary" className="text-xs" data-testid={`bundle-marker-qty-${b.bundle_barcode}`}>Marker Writing Operation Qty: {b.marker_qty}</Badge>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleRemoveBundle(b.bundle_barcode)}
                          className="text-red-500 hover:bg-red-50"
                          data-testid={`bundle-remove-${b.bundle_barcode}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default GreigePacking;
